import { useMemo } from 'react';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { FeatureTrend } from '@/types/feedback';
import { halfSplitDelta, trendDirection, type TrendDirection } from '@/lib/trend';

// Trend → bubble colour. Up is good (teal), down is bad (red), steady is neutral.
const TREND_COLOR: Record<TrendDirection, string> = {
  up: 'var(--chart-1, #14b8a6)',
  down: 'var(--destructive, #ef4444)',
  steady: 'var(--muted-foreground, #71717a)',
};

// Rating axis midpoint (1-5 scale) splitting "good" from "poor" sentiment.
const RATING_MIDPOINT = 3;

interface Point {
  feature: string;
  volume: number;
  rating: number;
  direction: TrendDirection;
}

/**
 * The hero: a quadrant scatter of feedback volume (X) vs average rating (Y),
 * bubble size = volume, bubble colour = trend direction. Quadrants read as
 * Protect / Fix first / Quiet wins / Watch.
 */
export function PriorityMap({
  features,
  onFeatureSelect,
}: {
  features: FeatureTrend[];
  onFeatureSelect?: (feature: string) => void;
}) {
  // Direction (bubble colour) is derived ONLY from each feature's own top-20 daily
  // series via halfSplitDelta — one source, everywhere. Do not add a second delta
  // source for the sub-top-20 tail "for completeness": top-20 membership churns at
  // the boundary as volume shifts between refreshes, so a second definition would
  // make a bubble change colour purely from crossing the bound. Keep it single-source
  // and let a feature simply appear/disappear at the edge.
  const points = useMemo<Point[]>(
    () =>
      features.map((f) => ({
        feature: f.stats.feature,
        volume: f.stats.count,
        rating: f.stats.averageRating,
        direction: trendDirection(halfSplitDelta(f.daily).delta),
      })),
    [features]
  );

  // Vertical divider at the mean volume separates high- from low-traffic features.
  const volumeDivider = useMemo(() => {
    if (points.length === 0) return 0;
    return points.reduce((sum, p) => sum + p.volume, 0) / points.length;
  }, [points]);

  const maxVolume = Math.max(1, ...points.map((p) => p.volume));

  return (
    <Card data-testid="priority-map">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">Priority map</CardTitle>
        <p className="text-xs text-muted-foreground">
          Volume vs rating · bubble size = volume · colour = trend
        </p>
      </CardHeader>
      <CardContent>
        {points.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">No feature data in this period</p>
        ) : (
          <div className="relative h-[320px]">
            {/* Quadrant labels (top-right Protect, bottom-right Fix first, etc.) */}
            <QuadrantLabels />
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ top: 16, right: 24, bottom: 24, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  type="number"
                  dataKey="volume"
                  name="Volume"
                  domain={[0, Math.ceil(maxVolume * 1.1)]}
                  tick={{ fontSize: 12 }}
                  className="text-muted-foreground"
                  label={{ value: 'Volume', position: 'insideBottom', offset: -12, fontSize: 12 }}
                />
                <YAxis
                  type="number"
                  dataKey="rating"
                  name="Avg rating"
                  domain={[1, 5]}
                  ticks={[1, 2, 3, 4, 5]}
                  tick={{ fontSize: 12 }}
                  className="text-muted-foreground"
                  label={{ value: 'Rating', angle: -90, position: 'insideLeft', fontSize: 12 }}
                />
                <ZAxis type="number" dataKey="volume" range={[60, 600]} />
                <ReferenceLine y={RATING_MIDPOINT} strokeDasharray="4 4" className="stroke-muted-foreground/40" />
                <ReferenceLine x={volumeDivider} strokeDasharray="4 4" className="stroke-muted-foreground/40" />
                <Tooltip cursor={{ strokeDasharray: '3 3' }} content={<PriorityTooltip />} />
                <Scatter
                  data={points}
                  fillOpacity={0.7}
                  onClick={(p: unknown) => {
                    const point = p as { feature?: string };
                    if (onFeatureSelect && point.feature) onFeatureSelect(point.feature);
                  }}
                  className={onFeatureSelect ? 'cursor-pointer' : undefined}
                >
                  {points.map((p) => (
                    <Cell key={p.feature} fill={TREND_COLOR[p.direction]} />
                  ))}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function QuadrantLabels() {
  const base = 'pointer-events-none absolute text-[11px] font-medium uppercase tracking-wide text-muted-foreground/60';
  return (
    <>
      <span className={`${base} right-6 top-2`}>Protect</span>
      <span className={`${base} left-10 top-2`}>Quiet wins</span>
      <span className={`${base} right-6 bottom-8`}>Fix first</span>
      <span className={`${base} left-10 bottom-8`}>Watch</span>
    </>
  );
}

function PriorityTooltip({ active, payload }: { active?: boolean; payload?: Array<{ payload: Point }> }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  const trendLabel = p.direction === 'up' ? '↑ improving' : p.direction === 'down' ? '↓ declining' : '→ steady';
  return (
    <div className="rounded-md border bg-popover px-3 py-2 text-xs shadow-md">
      <div className="font-medium">{p.feature}</div>
      <div className="text-muted-foreground">{p.volume} responses · ★ {p.rating.toFixed(1)}</div>
      <div className="text-muted-foreground">{trendLabel}</div>
    </div>
  );
}
